import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSyncAlt, FaTimes } from 'react-icons/fa';

type VersionPayload = {
  version?: string;
  updated_at?: string;
  label?: string;
};

const VERSION_ENDPOINT = '/version.json';
const VERSION_STORAGE_KEY = 'forfeo_explore_app_version';
const DISMISSED_STORAGE_KEY = 'forfeo_explore_app_update_dismissed_version';
const CHECK_INTERVAL_MS = 60_000;

function normalizeVersion(payload: VersionPayload | null): string {
  return String(payload?.version || payload?.updated_at || '').trim();
}

export default function AppUpdateBanner() {
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(() => {
    try {
      return localStorage.getItem(DISMISSED_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const isVisible = useMemo(() => {
    return Boolean(availableVersion && availableVersion !== dismissedVersion);
  }, [availableVersion, dismissedVersion]);

  const checkVersion = useCallback(async ({ silent = true } = {}) => {
    if (!silent) setChecking(true);

    try {
      const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      });

      if (!response.ok) return;

      const payload = await response.json() as VersionPayload;
      const nextVersion = normalizeVersion(payload);
      if (!nextVersion) return;

      let currentVersion: string | null = null;
      try {
        currentVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      } catch {
        currentVersion = null;
      }

      if (!currentVersion) {
        try {
          localStorage.setItem(VERSION_STORAGE_KEY, nextVersion);
        } catch {
          // Ignore storage errors in private mode.
        }
        return;
      }

      if (currentVersion !== nextVersion) {
        setAvailableVersion(nextVersion);
      }
    } catch {
      // Version checking must never block the app.
    } finally {
      if (!silent) setChecking(false);
    }
  }, []);

  useEffect(() => {
    checkVersion();

    const interval = window.setInterval(() => checkVersion(), CHECK_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };

    const onServiceWorkerUpdate = () => {
      checkVersion({ silent: false });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('forfeo-app-update', onServiceWorkerUpdate as EventListener);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('forfeo-app-update', onServiceWorkerUpdate as EventListener);
    };
  }, [checkVersion]);

  const refreshApp = async () => {
    if (availableVersion) {
      try {
        localStorage.setItem(VERSION_STORAGE_KEY, availableVersion);
        localStorage.removeItem(DISMISSED_STORAGE_KEY);
      } catch {
        // Ignore storage errors.
      }
    }

    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration('/');
        registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
        registration?.active?.postMessage({ type: 'CLEAR_FORFEO_CACHES' });
      } catch {
        // Ignore SW errors and reload anyway.
      }
    }

    window.location.reload();
  };

  const dismiss = () => {
    if (availableVersion) {
      try {
        localStorage.setItem(DISMISSED_STORAGE_KEY, availableVersion);
      } catch {
        // Ignore storage errors.
      }
      setDismissedVersion(availableVersion);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-5 left-1/2 z-[9999] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-2xl border border-blue-200 bg-white/95 p-3 shadow-2xl shadow-slate-900/15 backdrop-blur-xl md:bottom-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/25">
          <FaSyncAlt className={checking ? 'animate-spin' : ''} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black text-slate-900">Nouvelle version Forfeo disponible</p>
          <p className="text-xs font-semibold text-slate-500">Actualise pour charger les derniers correctifs du site.</p>
        </div>
        <button
          type="button"
          onClick={refreshApp}
          className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-wide text-white transition hover:bg-slate-800"
        >
          Actualiser
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
}
